/**
 * Núcleo de condiciones alineado con `views-single-view` → `dynamic-rules-engine.js`
 * y con `zProjectManagement/refactor-conditionalRules/refactor-conditionalRules.md`
 * (operadores canónicos en MAYÚSCULAS). Cuando exista `@trazit/condition-engine`, sustituir por import.
 */

/**
 * @param {Object} condition
 * @param {Object} dataItem
 * @returns {boolean}
 */
export function evaluateCondition(condition, dataItem) {
  if (!condition || !dataItem || typeof dataItem !== 'object') {
    return false;
  }

  const val = dataItem?.[condition.field];

  switch (condition.operator) {
    case 'EQUALS':
      if (typeof val === 'boolean' || typeof condition.value === 'boolean') {
        const valBool = val === true || val === 1 || val === 'true' || val === '1';
        const conditionBool =
          condition.value === true ||
          condition.value === 1 ||
          condition.value === 'true' ||
          condition.value === '1';
        return valBool === conditionBool;
      }
      return String(val) === String(condition.value);

    case 'NOT_EQUALS':
      return String(val) !== String(condition.value);

    case 'ISNULL':
      return val === null || val === undefined || val === '';

    case 'ISNOTNULL':
      return val !== null && val !== undefined && val !== '';

    case 'CONTAINS':
      return String(val).includes(String(condition.value));

    case 'NOT_CONTAINS':
      return !String(val).includes(String(condition.value));

    case 'EXISTS':
      return condition.field in dataItem;

    case 'NOT_EXISTS':
      return !(condition.field in dataItem);

    case 'EXISTS_NOT_EMPTY':
      if (!(condition.field in dataItem)) return false;
      if (val === null || val === undefined) return false;
      if (typeof val === 'string' && val.trim() === '') return false;
      if (Array.isArray(val) && val.length === 0) return false;
      if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0)
        return false;
      return true;

    case 'EXISTS_HAS_PROPERTIES':
      if (!(condition.field in dataItem)) return false;
      if (typeof val !== 'object' || val === null || Array.isArray(val)) return false;
      if (!Array.isArray(condition.propertiesList)) return false;
      return condition.propertiesList.every((prop) => prop in val);

    case 'LESS_THAN':
      return Number(val) < Number(condition.value);

    case 'LESS_OR_EQUAL':
      return Number(val) <= Number(condition.value);

    case 'GREATER_THAN':
      return Number(val) > Number(condition.value);

    case 'GREATER_OR_EQUAL':
      return Number(val) >= Number(condition.value);

    case 'IN':
      if (!Array.isArray(condition.value)) return false;
      return condition.value.includes(String(val)) || condition.value.includes(Number(val));

    case 'NOT_IN':
      if (!Array.isArray(condition.value)) return true;
      return !condition.value.includes(String(val)) && !condition.value.includes(Number(val));

    default:
      console.warn(`[condition-rules-eval] Operador desconocido: ${condition.operator}`);
      return false;
  }
}

/**
 * AND lógico. Array vacío → true.
 * @param {Array<Object>} conditions
 * @param {Object} dataItem
 * @returns {boolean}
 */
export function evaluateConditions(conditions, dataItem) {
  if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
    return true;
  }
  if (!dataItem || typeof dataItem !== 'object') {
    return false;
  }
  return conditions.every((c) => evaluateCondition(c, dataItem));
}

/**
 * Acepta `field` o `property` (como filterEntries de master data / vistas).
 * @param {Object} rule
 * @returns {Object|null}
 */
export function normalizeConditionRule(rule) {
  if (!rule || typeof rule !== 'object') return null;
  const field = rule.field ?? rule.property;
  if (field == null || field === '') return null;
  return { ...rule, field };
}

/** @param {unknown[]} rules */
export function normalizeConditionRules(rules) {
  if (!Array.isArray(rules)) return [];
  return rules.map(normalizeConditionRule).filter(Boolean);
}
